// ═══════════════════════════════════════════════════════════════════
// Currency availability overlay (admin registry → UI lists)
// ═══════════════════════════════════════════════════════════════════
// public.platform_currencies decides WHICH currencies are offered and for
// WHICH surface. src/lib/money.ts (→ _shared/money/presentation.ts) stays the
// only conversion/formatting authority. No page may hand-maintain its own
// currency array — every list is derived from this hook.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSupportedCurrencies, type CurrencyOption } from "@/lib/money";

export type CurrencyCapability =
  | "courier_operational"
  | "customer_viewing"
  | "pricing"
  | "quote"
  | "finance"
  | "reporting";

export interface CurrencyRegistryRow {
  code: string;
  name: string;
  symbol: string;
  symbol_position: "before" | "after";
  decimal_places: number;
  sort_order: number;
  enabled: boolean;
  courier_operational_enabled: boolean;
  customer_viewing_enabled: boolean;
  pricing_enabled: boolean;
  quote_enabled: boolean;
  finance_enabled: boolean;
  reporting_enabled: boolean;
  fx_available: boolean;
  fx_status: string | null;
  fx_checked_at: string | null;
  notes: string | null;
  updated_at: string;
}

const CAPABILITY_COLUMN: Record<CurrencyCapability, keyof CurrencyRegistryRow> = {
  courier_operational: "courier_operational_enabled",
  customer_viewing: "customer_viewing_enabled",
  pricing: "pricing_enabled",
  quote: "quote_enabled",
  finance: "finance_enabled",
  reporting: "reporting_enabled",
};

export function useCurrencyRegistry() {
  return useQuery({
    queryKey: ["platform-currencies"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<CurrencyRegistryRow[]> => {
      const { data, error } = await supabase
        .from("platform_currencies")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as CurrencyRegistryRow[];
    },
  });
}

/**
 * Currencies offered for one surface. While the registry is loading (or if it
 * is unreachable) we fall back to the historically offered set, so no surface
 * ever renders an empty currency list.
 */
export function useAvailableCurrencies(capability: CurrencyCapability): {
  options: CurrencyOption[];
  codes: string[];
  isLoading: boolean;
} {
  const { data, isLoading } = useCurrencyRegistry();
  const column = CAPABILITY_COLUMN[capability];
  const codes = (data || [])
    .filter((row) => row.enabled && row[column] === true)
    .map((row) => row.code);
  const options = getSupportedCurrencies(codes.length ? codes : null);
  return { options, codes: options.map((o) => o.code), isLoading };
}
